import {
  gql,
  NetworkStatus,
  OperationVariables,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  ActionButton,
  DirectionalHint,
  IColumn,
  IDetailsRowProps,
  IRenderFunction,
  Stack,
  TooltipHost,
} from '@fluentui/react';
import { AttachDocumentModal } from 'common/components/AttachDocumentModal';
import { showCustomToast } from 'common/components/CustomToast/utils';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { DocumentViewModal } from 'common/components/DocumentList/DocumentViewModal';
import { TABLE_ROWS } from 'common/constants';
import { EntityDocumentFilter } from 'common/types/globalTypes';
import { SortOrder } from 'common/utils/commonTypes';
import { DocumentNode } from 'graphql';
import { JSX, useCallback, useState } from 'react';
import { useStyles } from './index.styles';

// Base interface for query variables
interface BaseAttachQueryVariables extends OperationVariables {
  first?: number | undefined | null;
  entityId?: string | null;
  orderBy?: string[] | null | undefined | string;
  after?: string | null;
  filter?: EntityDocumentFilter | null;
  documentTypeId?: number | null;
}

// Generic types for the component
interface GenericAttachableData {
  id: string;
  fileReference?: string | null;
  _uploadDate?: string | null;
  _isAccountingDocument?: boolean | null;
  _isAppliableDocumentType?: boolean | null;

  _fileType?: string | null;
  _isProtected?: boolean | null;
  _fileViewer?: string | null;
  [key: string]: any; // Allow additional fields
}

interface GenericAttachFormProps<
  TData extends GenericAttachableData,
  TQueryData,
  TQueryVariables extends BaseAttachQueryVariables,
  TMutationData,
  TMutationVariables extends OperationVariables,
  TDocumentType
> {
  attachDocumentsMutation?: DocumentNode;

  entity: { id?: string | null; isDraft?: boolean | null; [key: string]: any };
  buttonRightAligned?: boolean;
  buttonPaddingTop?: number;
  onAttachedSuccessfully?: (selectedItems?: TData[]) => void; // Updated to accept selectedItems
  availableDocumentTypes?: TDocumentType;
  attachableDocumentsQuery: DocumentNode;
  detailsQuery: DocumentNode;
  columns: IColumn[];
  modalWidth?: number;
  isDirty?: boolean; // Added prop to control ActionButton disabled state
  getDocumentTypeOptions: (data: TDocumentType | undefined) => Array<{
    key: string | number;
    text: string;
    title?: string;
    disabled?: boolean;
  }>;
  transformData?: (data: TQueryData | undefined) => TData[] | undefined;
  canSelectItem?: (item: TData) => boolean;
  getMutationInput: (
    selectedItems: TData[],
    entityId: string,
    documentTypeId?: number | null
  ) => TMutationVariables;
  onRenderItemColumn: (
    item?: TData,
    index?: number,
    column?: IColumn,
    openDocumentViewModal?: (state: DocumentViewModalState) => void
  ) => JSX.Element | undefined;
  initialQueryVariables?: Partial<TQueryVariables>;
  toOrderByVariable: (sort?: SortOrder) => string[] | null;
}

export const GenericAttachForm = <
  TData extends GenericAttachableData,
  TQueryData,
  TQueryVariables extends BaseAttachQueryVariables,
  TMutationData,
  TMutationVariables extends OperationVariables,
  TDocumentType
>({
  entity,
  buttonRightAligned,
  buttonPaddingTop,
  availableDocumentTypes,
  attachableDocumentsQuery,
  attachDocumentsMutation,
  onAttachedSuccessfully,
  detailsQuery,
  columns,
  modalWidth = 1350,
  isDirty = false, // Default to false if not provided
  getDocumentTypeOptions,
  transformData,
  canSelectItem,
  getMutationInput,
  onRenderItemColumn,
  initialQueryVariables,
  toOrderByVariable,
}: GenericAttachFormProps<
  TData,
  TQueryData,
  TQueryVariables,
  TMutationData,
  TMutationVariables,
  TDocumentType
>) => {
  const [dialogVisible, setDialogVisible] = useState(false);
  return (
    <>
      <Stack
        horizontal
        styles={{
          root: {
            height: 44,
            paddingRight: buttonRightAligned ? 20 : undefined,
            paddingTop: buttonPaddingTop ? buttonPaddingTop : undefined,
          },
        }}
        horizontalAlign={buttonRightAligned ? 'end' : undefined}
      >
        <TooltipHost content="Attach new files">
          <ActionButton
            onClick={() => {
              setDialogVisible(true);
            }}
            iconProps={{ iconName: 'Attach' }}
            text={buttonRightAligned ? 'Documents' : 'Attach'}
            checked
            disabled={isDirty}
          />
        </TooltipHost>
      </Stack>
      {dialogVisible && (
        <>
          <GenericAttachFormModal
            entity={entity}
            availableDocumentTypes={availableDocumentTypes}
            attachableDocumentsQuery={attachableDocumentsQuery}
            attachDocumentsMutation={attachDocumentsMutation}
            onAttachedSuccessfully={onAttachedSuccessfully}
            detailsQuery={detailsQuery}
            columns={columns}
            modalWidth={modalWidth}
            getDocumentTypeOptions={getDocumentTypeOptions}
            transformData={transformData}
            canSelectItem={canSelectItem}
            getMutationInput={getMutationInput}
            onRenderItemColumn={onRenderItemColumn}
            initialQueryVariables={initialQueryVariables}
            toOrderByVariable={toOrderByVariable}
            setOpen={setDialogVisible}
          />
        </>
      )}
    </>
  );
};

interface GenericAttachFormModalProps<
  TData extends GenericAttachableData,
  TQueryData,
  TQueryVariables extends BaseAttachQueryVariables,
  TMutationData,
  TMutationVariables extends OperationVariables,
  TDocumentType
> extends Omit<
    GenericAttachFormProps<
      TData,
      TQueryData,
      TQueryVariables,
      TMutationData,
      TMutationVariables,
      TDocumentType
    >,
    'availableDocumentTypesQuery' | 'isDirty' // Exclude isDirty from modal props
  > {
  availableDocumentTypes: TDocumentType | undefined;
  setOpen: (open: boolean) => void;
}

const GenericAttachFormModal = <
  TData extends GenericAttachableData,
  TQueryData,
  TQueryVariables extends BaseAttachQueryVariables,
  TMutationData,
  TMutationVariables extends OperationVariables,
  TDocumentType
>({
  entity,
  availableDocumentTypes,
  attachableDocumentsQuery,
  attachDocumentsMutation,
  detailsQuery,
  columns,
  modalWidth,
  getDocumentTypeOptions,
  transformData,
  canSelectItem,
  getMutationInput,
  onRenderItemColumn,
  initialQueryVariables,
  toOrderByVariable,
  setOpen,
  onAttachedSuccessfully,
}: GenericAttachFormModalProps<
  TData,
  TQueryData,
  TQueryVariables,
  TMutationData,
  TMutationVariables,
  TDocumentType
>) => {
  const styles = useStyles();
  const [selectedList, setSelectedList] = useState<TData[]>([]);
  const [docViewState, setDocViewState] = useState<DocumentViewModalState>({
    isOpen: false,
    _fileType: 'pdf',
  });
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<
    number | null
  >(null);

  const openDocumentViewModal = useCallback((state: DocumentViewModalState) => {
    setDocViewState(state);
  }, []);

  const defaultQueryVariables: TQueryVariables = {
    first: TABLE_ROWS,
    entityId: entity.id,
    orderBy: ['_UPLOAD_DATE_DESC', 'PRIMARY_KEY_ASC'],
    ...initialQueryVariables,
  } as TQueryVariables;

  const {
    data: documentsList,
    loading: documentsLoading,
    fetchMore,
    variables: documentsVariables,
    networkStatus,
    refetch,
  } = useQuery<TQueryData, TQueryVariables>(attachableDocumentsQuery, {
    variables: defaultQueryVariables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const DUMMY_MUTATION = gql`
    mutation DummyAttach {
      _dummy
    }
  `;

  const [mutationFn, { loading: attachLoading, error: attachError }] =
    useMutation<TMutationData, TMutationVariables>(
      (attachDocumentsMutation ?? DUMMY_MUTATION) as DocumentNode,
      {
        errorPolicy: 'all',
      }
    );

  // Optional wrapper that checks if mutation should run
  const attachDocuments = async (variables: TMutationVariables) => {
    if (!attachDocumentsMutation || !entity.id) {
      return Promise.resolve(); // Don't run if mutation is not provided
    }
    return mutationFn({
      variables,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: detailsQuery,
          variables: { id: entity.id },
        },
      ],
    });
  };

  const documentTypeOptions = getDocumentTypeOptions(availableDocumentTypes);

  const transformedData = transformData
    ? transformData(documentsList)
    : (documentsList as any)?.attachablePaymentDocuments?.nodes ||
      (documentsList as any)?.appliableInvoiceSigningDocuments?.nodes;
  const _onAttach = async () => {
    if (!attachDocumentsMutation || !entity.id) {
      console.warn('Missing attachDocumentsMutation or entity.id');
      onAttachedSuccessfully?.(selectedList); // Pass selectedList
      setOpen(false);
      return;
    }

    const input = getMutationInput(
      selectedList,
      entity.id,
      selectedDocumentTypeId
    );

    try {
      const result = await attachDocuments(input);

      if (
        result &&
        'errors' in result &&
        (result.errors?.length || attachError)
      ) {
        showCustomToast({
          content: result.errors?.[0]?.message || 'Cannot attach the document',
          appearance: 'error',
        });
      } else {
        showCustomToast({
          content: 'Document attached successfully',
          appearance: 'success',
        });
        setOpen(false);
        onAttachedSuccessfully?.(selectedList); // Pass selectedList
      }
    } catch (err) {
      showCustomToast({
        content: 'Unexpected error occurred during document attachment',
        appearance: 'error',
      });
    }
  };

  const onRenderRow: IRenderFunction<IDetailsRowProps> = (
    props,
    defaultRender
  ) => {
    if (!props) return null;
    const { item } = props;
    const tooltipVisible = !entity.isDraft && item._isAccountingDocument;
    const tooltipMessage =
      'Upload prohibited during/after the approval process';
    return (
      <TooltipHost
        content={tooltipVisible ? tooltipMessage : ''}
        directionalHint={DirectionalHint.rightCenter}
      >
        {defaultRender!({ ...props, className: styles.row })}
      </TooltipHost>
    );
  };

  const loadMore = useCallback(async () => {
    try {
      await fetchMore({
        variables: {
          ...documentsVariables,
          after:
            (documentsList as any)?.pageInfo?.endCursor ||
            (documentsList as any)?.attachablePaymentDocuments?.pageInfo
              ?.endCursor ||
            (documentsList as any)?.appliableInvoiceSigningDocuments?.pageInfo
              ?.endCursor,
        },
      });
    } catch (err) {
      console.error('Error during fetchMore:', err);
    }
  }, [fetchMore, documentsVariables, documentsList]);

  const onFiltersReload = useCallback(
    async (filter: EntityDocumentFilter | undefined) => {
      try {
        await refetch({ ...documentsVariables, filter } as TQueryVariables);
      } catch (err) {
        console.error('Error during refetch with filter:', err);
      }
    },
    [documentsVariables, refetch]
  );

  const reload = useCallback(
    async (sort?: SortOrder) => {
      try {
        await refetch({
          ...documentsVariables,
          after: null,
          orderBy: toOrderByVariable(sort),
        } as TQueryVariables);
      } catch (err) {
        console.error('Error during reload with sort:', err);
      }
    },
    [refetch, documentsVariables, toOrderByVariable]
  );

  const onDocumentTypeReload = useCallback(
    async (documentTypeId: number | null) => {
      setSelectedDocumentTypeId(documentTypeId);
      try {
        await refetch({
          ...documentsVariables,
          documentTypeId,
        } as TQueryVariables);
      } catch (err) {
        console.error('Error during refetch with documentTypeId:', err);
      }
    },
    [refetch, documentsVariables]
  );

  const attachDisabled = selectedList.length === 0;
  return (
    <>
      <AttachDocumentModal
        columns={columns}
        modalWidth={modalWidth}
        loading={documentsLoading}
        items={
          networkStatus === NetworkStatus.refetch ||
          networkStatus === NetworkStatus.setVariables
            ? undefined
            : transformedData
        }
        hasNextPage={
          (documentsList as any)?.pageInfo?.hasNextPage ||
          (documentsList as any)?.attachablePaymentDocuments?.pageInfo
            ?.hasNextPage ||
          (documentsList as any)?.appliableInvoiceSigningDocuments?.pageInfo
            ?.hasNextPage
        }
        attachLoading={attachLoading}
        availableDocumentTypes={documentTypeOptions}
        setOpen={setOpen}
        onSortReload={reload}
        onLoadMore={loadMore}
        onFiltersReload={onFiltersReload}
        onDocumentTypeChange={onDocumentTypeReload}
        attachDisabled={attachDisabled}
        onSelectionChanged={setSelectedList}
        dropdownDisabled={selectedList.length > 0}
        onRenderItemColumn={(item, index, column) =>
          onRenderItemColumn(item, index, column, openDocumentViewModal)
        }
        onRenderRow={onRenderRow}
        onAttachDocuments={_onAttach}
        canSelectItem={canSelectItem}
        isDraggable
      />
      <DocumentViewModal
        onDismiss={() => setDocViewState({ isOpen: false, _fileType: 'pdf' })}
        {...docViewState}
      />
    </>
  );
};
